import { downloadFile, formatNumber } from '@/core/helpers';
import DownloadIcon from '@/icons/download.svg?react';

import FileIcon from '../FileIcon';
import { APIEventsEnum, useSendEvent } from '../../hooks';
import { getReportResultFileLink } from '@/core/api';
import { useTranslation } from 'react-i18next';
import { AuthedRoutesEnum } from '@/core/enums';
import { Link, useLocation } from 'react-router';
import {
  DatevEntryTypesMapping,
  DatevFileMappingDE,
  DatevFileMappingEN,
} from './Datev.helpers';
import { useNPSModal } from '../NPSModal/useNPSModal';
import OverviewIcon from '@/icons/assesment.svg?react';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';

export interface DatevTileItem {
  option: string;
  asciiFilename: string;
  extfFilename: string;
  asciiFileId: number;
  extfFileId: number;
}

interface Props {
  item: DatevTileItem;
  companyId: string;
  count?: number;
  period?: Date;
}

export default function NewDatevItem({
  item,
  companyId,
  count,
  period,
}: Props) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { openModal } = useNPSModal();
  const { sendEvent } = useSendEvent();
  const { fibu224ShowExtf } = useFlags();

  const onClickDownload = (filename: string, fileId: number) => () => {
    const apiEvent = APIEventsEnum.download_datev_file;

    sendEvent(apiEvent, { file: filename });
    getReportResultFileLink(fileId, companyId)
      .then((link) => {
        downloadFile(link, filename);
        openModal(apiEvent);
      })
      .catch(console.error);
  };

  const params = new URLSearchParams(location.search);
  params.set('company_id', companyId);
  params.set('entry_type', String(DatevEntryTypesMapping[item.option]));
  params.set('page', '1');

  const DatevFileMapping =
    i18n.language === 'de' ? DatevFileMappingDE : DatevFileMappingEN;

  if (!item.asciiFilename && !item.extfFilename) {
    return (
      <div className='flex items-center px-4 py-2'>
        <div className='flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 p-1'>
          <FileIcon ext='empty' />
        </div>
        <div className='flex w-full justify-between gap-x-2 truncate pl-2 font-semibold text-gray-700'>
          {DatevFileMapping[item.option]}
          <span>{t('No data available')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className='grid grid-cols-[2fr,1fr,1fr] gap-12 px-4 py-2'>
      <div className='flex items-center truncate'>
        <div className='flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 p-1'>
          <FileIcon ext={item.asciiFilename.split('.').slice(-1)[0]} />
        </div>
        <div className='truncate pl-2 font-semibold'>
          {DatevFileMapping[item.option]}
        </div>
      </div>
      {period && (
        <Link
          className='group flex cursor-pointer items-center gap-x-2 font-semibold hover:text-green'
          to={{
            pathname: AuthedRoutesEnum.DatevOverview.replace(
              ':year',
              String(period.getFullYear())
            ).replace(':month', String(period.getMonth() + 1)),
            search: params.toString(),
          }}
        >
          <OverviewIcon className='[&>path]:transition-[fill] group-hover:[&>path]:fill-green' />
          <span>{count !== undefined && formatNumber(count)}</span>
        </Link>
      )}
      <div className='flex items-center justify-end gap-x-4'>
        <button
          className='group flex items-center justify-center gap-x-2 rounded-full transition-colors hover:text-green disabled:cursor-not-allowed disabled:text-gray-700'
          onClick={onClickDownload(item.asciiFilename, item.asciiFileId)}
          data-testid={
            item.asciiFileId ? `download-datev-${item.asciiFileId}` : undefined
          }
          disabled={!item.asciiFilename || !item.asciiFileId}
        >
          <DownloadIcon
            className={classNames(
              'h-6 w-6 shrink-0 [&>path]:fill-dark [&>path]:transition-[fill] ',
              {
                '[&>path]:fill-gray-700':
                  !item.asciiFilename || !item.asciiFileId,
                'group-hover:[&>path]:fill-green':
                  item.asciiFilename && item.asciiFileId,
              }
            )}
          />
          <span>ASCII</span>
        </button>
        {fibu224ShowExtf && (
          <button
            className='group flex items-center justify-center gap-x-2 rounded-full transition-colors hover:text-green disabled:cursor-not-allowed disabled:text-gray-700'
            onClick={onClickDownload(item.extfFilename, item.extfFileId)}
            data-testid={
              item.extfFileId ? `download-datev-${item.extfFileId}` : undefined
            }
            disabled={!item.extfFilename || !item.extfFileId}
          >
            <DownloadIcon
              className={classNames(
                'h-6 w-6 shrink-0 [&>path]:fill-dark [&>path]:transition-[fill] ',
                {
                  '[&>path]:fill-gray-700':
                    !item.extfFilename || !item.extfFilename,
                  'group-hover:[&>path]:fill-green':
                    item.extfFilename && item.extfFilename,
                }
              )}
            />
            <span>EXTF</span>
          </button>
        )}
      </div>
    </div>
  );
}
